import { POrtho, PSlash, PSquare } from "../widgets/Delimit";
import { Rule } from "../widgets/Rule";
import { RulesTable } from "../widgets/RulesTable";
import { Say } from "../widgets/Say";
import { Say2 } from "../widgets/Say2";
import { RuleSummary } from "./RuleSummary";
import { SeeFeatures } from "./SeeFeatures";

export function RulesEssay() {
  return (<div className='page'>
    <h1>Sound Change Rules</h1>

    <p>
      The sounds and words spoken by groups of people tend to shift over time,
      leading to new accents, new dialects, and - eventually - new languages.
      Linguists have found that sounds tend to change consistently for a given group of people
      in ways that let us write rules for these sound changes.
    </p>

    <p>
      This essay discusses how <Say>/ˈfoʊ̯nim ˌʃɪftɝ/</Say> uses rules to describe sound changes
      or to convert from a writing system (orthography) to sounds (phonemes).
      Note that you can click when you see <Say say="/ɐ ˈtɹaɪ̯ˌɛə̯ŋɡɫ̩ ˈfɑɫɤ̞o̯d baɪ̯ ˈɐndɝ.ˌɫaɪ̯nd ˈtɛkst/">a
        triangle followed by underlined text</Say> to hear it spoken.
      The symbols used for describing pronunciations are in IPA, the International Phonetic Alphabet.
    </p>

    <p>
      Quick links to the sections:
    </p>

    <ul>
      <li><a href="#simple">Simple changes</a></li>
      <li><a href="#context">Context</a></li>
      <li><a href="#logic">Or, not, and</a></li>
      <li><a href="#features">Features</a></li>
      <li><a href="#stress">Stress</a></li>
      <li><a href="#swap">Swap</a></li>
      <li><a href="#reset">Resetting tracking</a></li>
      <li><a href="#summary">Summary</a></li>
    </ul>


    <h2 id="simple">Simple changes</h2>

    <p>
      Proto-Indo-European (PIE) is a reconstructed language from a few thousand years ago that has
      changed and split many times over the centuries, giving rise to languages such as English, Hindi, Spanish, Greek, and Czech.
      One of the early successes of linguistics was recognizing common sound patterns across
      a wide variety of languages, which they used to guess at what PIE must have sounded like long ago,
      and how it changed into the large number of modern languages.
    </p>

    <p>
      It's customary to mark a reconstructed PIE word with an asterisk.
      For example the word for <i>ten</i> was something like <i>*deḱm̥</i>.
      We'll look at how the original sounds changed in later languages.
    </p>

    <p>
      In Old English, PIE <i>*d</i> typically changed to <i>t</i>.
      We write this as <i>{`*d > t`}</i>.
      Other changes include <i>{`*ḱ > h`}</i> and <i>{`*m̥ > um`}</i>.
      We'll drop the asterisk when writing the rules since we're describing how to change one symbol (or symbols) into other symbols.
      This gives us the following rules for PIE to Old English:
    </p>

    <RulesTable rules={[
      { rule: "d > t" },
      { rule: "ḱ > h" },
      { rule: "m̥ > um" },
    ]} />

    <p>
      When we apply these rules to PIE <Say say="[ˈdekʲə̆m]">*deḱm̥</Say>,
      we would predict Old English <Say say="[ˈtehum]">tehum</Say>, close to the actual word <Say language="Old English">tīen</Say>.
      (Keep in mind that there were a few thousand years between PIE and Old English, so this isn't a bad prediction for such simple rules.)
      This turned into the modern word <Say>ten</Say>.
    </p>

    <p>
      Contrast this with the rules for turning PIE into Latin:
    </p>

    <RulesTable rules={[
      { rule: "d > d" },
      { rule: "ḱ > c" },
      { rule: "m̥ > em" },
    ]} />

    <p>
      When applied, this gives us Latin <Say say="[ˈdekem]">decem</Say>.
      Latin and English started from the same sounds, but changed them in different ways to end up with words that sound very different.
    </p>

    <p>
      Here are a few more simplified PIE to Old English rules:
    </p>

    <RulesTable rules={[
      { rule: "dw > tw" },
      { rule: "o > a" },
      { rule: "t > þ" },
      { rule: "ei > ī" },
    ]} />

    <p>
      This predicts that PIE <Say say="[ˈdwo]">*dwo</Say> turned into <Say language="Old English">twa</Say>.
      Old English actually had a few different forms of two based on gender and case,
      which included the predicted <Say language="Old English">tƿā</Say> (where <PSlash>ƿ</PSlash> sounds like <PSlash>w</PSlash>)
      and a modern sounding <Say language="Old English">tū</Say>,
      which turned into <Say say="[ˈtʰɯu̯]">two</Say> (oh, <i>that's</i> where that weird spelling came from!).
      And it predicts PIE <Say say="[ˈtrei]">*trei</Say> turned
      into <Say language="Old English">þrī</Say> (where <PSlash>þ</PSlash> sounds like <PSlash>θ</PSlash> or <PSlash>ð</PSlash>),
      very close to modern <Say say="[ˈθɹiː]">three</Say>.
    </p>

    <p>
      References for more information:
    </p>

    <ul>
      <li><a href="https://en.wikipedia.org/wiki/Proto-Indo-European_numerals">PIE numerals</a></li>
      <li><a href="https://en.wikipedia.org/wiki/Indo-European_sound_laws">PIE sound laws</a></li>
      <li><a href="https://en.wikibooks.org/wiki/Old_English/Numbers">Old English numbers</a></li>
    </ul>


    <h2 id="context">Context</h2>

    <p>
      Typically, one sound doesn't <i>always</i> turn into another sound, instead depending on context.
      For example, it may depend on whether the sound is at the beginning or end of a word,
      in between two vowels, or before a certain consonant.
    </p>

    <p>
      For example, in a Cockney accent, an <i>h</i> at the start of a word turned into, <i>ʔ</i>, a glottal stop.
      Thus, <Say say="/maɪ̯ ˈhæt/">my hat</Say> sounds like <Say>/mi ˈʔɛt/</Say>.
      This rule is written as <Rule>{`h > ʔ / #_`}</Rule> , which consists of the following pieces:
    </p>

    <RulesTable rules={[
      { rule: "h > ʔ", other: <>the rule: <i>h</i> changes to <i>ʔ</i></> },
      { rule: "/", other: <>the context in which the change occurs is written after the slash</> },
      {
        rule: "#_", other: <>context: at the start of a word.<br />
          # represents a word boundary and _ represents the symbol(s) being changed,
          so together this means that there's a word boundary right before the <i>h</i></>
      },
    ]} />

    <p>
      In a Scouse (Liverpool) accent, an initial <i>h</i> may disappear completely.
      We use ∅ to represent that a symbol turns into nothing, so the rule looks like this:
    </p>

    <RulesTable rules={[
      { rule: "h > ∅ / #_", other: <><i>h</i> goes away when at the start of a word</> },
    ]} />

    <p>
      Some English accents will turn a trailing -ing into -in, for example,
      changing <Say say="/ˈtʰɑkɪŋ/">talking</Say> into <Say say="/ˈtʰɑkɪn/">talkin</Say>.
      But an <i>ing</i> in other parts of the word stays the same, such as <Say say="/ˈɪŋɡət/">ingot</Say>.
      We indicate that it only changes if it's at the end of the word using <Rule>/ _#</Rule>, giving us:
    </p>

    <RulesTable rules={[
      { rule: "ɪŋ > ɪn / _#", other: <>the change only occurs at the end of a word</> },
    ]} />

    <p>
      When describing context, you can use <i>V</i> to represent any vowel and <i>C</i> to represent any consonant.
      For example, in "non-rhotic" accents, r-sounds go away in some contexts:
      after a vowel but before a consonant, described as <Rule>V_C</Rule>,
      and after a vowel at the end of a word, described as <Rule>V_#</Rule>.
      These cases can be captured with the following rules:
    </p>

    <RulesTable rules={[
      { rule: "ɹ > ∅ / V_C", other: <><Say say="/ˈɹa/">ɹ</Say> goes away when preceded by a vowel and followed by a consonant</> },
      { rule: "ɹ > ∅ / V_#", other: <><Say say="/ˈɹa/">ɹ</Say> goes away when preceded by a vowel and followed by the end of the word</> },
    ]} />

    <p>
      These rules will turn start: <Say>/ˈstɑɹt/</Say> into <Say>/ˈstɑt/</Say> and star: <Say>/ˈstɑɹ/</Say> into <Say>/ˈstɑ/</Say>,
      but will leave strawberry: <Say>/ˈstɹɑˌbɛɹi/</Say> unchanged.
    </p>

    <p>
      You can also reference specific symbols in the context.
      For example, to say a final <i>d</i> is dropped when preceded by an <i>n</i>, you'd say <Rule>{`d > ∅ / n_#`}</Rule>.
    </p>


    <h2 id="logic">Or, not, and</h2>

    <p>
      Sometimes it's useful to be able to specify more complex context in order to make the rules shorter and easier to understand.
    </p>

    <p>
      For example, to make words non-rhotic, we needed two rules: one for V_C and one for V_#.
      We can instead combine this into a single rule that says, "when preceded by a vowel and followed by a consonant OR the end of the word".
      To do this, wrap <i>C</i> and <i>#</i> in curly braces and separate them with a vertical bar: <Rule>{`{C|#}`}</Rule>.
    </p>

    <RulesTable rules={[
      { rule: "ɹ > ∅ / V_{C|#}", other: <><Say say="/ˈɹa/">ɹ</Say> goes away when preceded by a vowel and followed by a consonant or end-of-word</> },
    ]} />

    <p>
      Another way to say "consonant OR end-of-word" is "NOT a vowel".
      To indicate NOT, put an exclamation mark in front of the condition: <Rule>!V</Rule>.
    </p>

    <RulesTable rules={[
      { rule: "ɹ > ∅ / V_!V", other: <><Say say="/ˈɹa/">ɹ</Say> goes away when preceded by a vowel and followed by something that's not a vowel</> },
    ]} />

    <p>
      Similar to OR, there are times where you may want to say <i>this</i> AND <i>that</i>.
      As with OR, wrap the conditions in curly braces, but instead separate them with an ampersand: <Rule>{`{a&b}`}</Rule>.
      We'll see a case where this is useful in the next section.
    </p>


    <h2 id="features">Features</h2>

    <p>
      You can also specify phonetic <i>features</i> in rules.
    </p>

    <p>
      Let's say your language contains the following vowel sounds:&nbsp;
      <Say2>eː</Say2>, <Say2>ɚ</Say2>, <Say2>æː</Say2>, <Say say="[ˈæ]">æ</Say>, <Say2>uː</Say2>, and <Say say="[ˈu]">u</Say>,
      where ː indicates that the vowel is <i>long</i>.
      If you have sound change rules that vary depending on whether or not the vowel is long,
      you could either explicitly list the vowels, which can be error prone as you refine your language and its accents,
      or you could simply have your context specify <Rule>[+long]</Rule> for long vowels and <Rule>![+long]</Rule> otherwise.
    </p>

    <p>
      For example, if you want to say that <i>t</i> changes to <i>d</i> when
      the sound before it is short, you could use the rule <Rule>{`t > d / ![+long]_`}</Rule>.
      However, if your language also has long <i>consonants</i>,
      you'll want to explicitly say that you want short <i>vowels</i>, which looks like this:
    </p>

    <RulesTable rules={[
      { rule: "t > d / {V&![+long]}_#" },
    ]} />

    <p>
      To see the features associated with given symbols, enter IPA into the following box.
      Any of the listed features can be used in rules.
      Note that <Rule>V</Rule> and <Rule>C</Rule> are shorthand for <Rule>[+vowel]</Rule> and <Rule>[+consonant]</Rule> respectively.
    </p>

    <SeeFeatures />


    <h2 id="stress">Stress</h2>

    <p>
      Sometimes a rule should only be applied based on whether or not the current syllable or surrounding syllables are stressed.
      But in order for <Say>/ˈfoʊ̯nim/</Say> to be able to do that, it first needs to know where the syllables are.
    </p>

    <p>
      In IPA, syllables are separated by periods.
      For example, <Say>/ɪɡ.ˈzæm.pəɫ/</Say> contains periods that separate the word into three syllables,
      with a primary stress marker on the middle syllable.
      If the input IPA includes syllable separators and stress markers, the rules can simply use them.
      Otherwise, the system will need to be able to figure out where the syllable breaks are.
    </p>

    <p>
      Syllables in various languages tend to follow a similar pattern of sounds,
      called the <a href="https://en.wikipedia.org/wiki/Sonority_hierarchy">sonority hierarchy</a>.
      However, a lot of languages have their own quirks that are captured in
      language-specific <a href="https://en.wikipedia.org/wiki/Phonotactics">phonotactics</a>,
      which describe what combination of sounds are permitted in its words and syllables.
      But that's beyond the scope of this essay,
      where we'll assume either that syllable breaks and stress markers are already part of the IPA input,
      or they can be derived using the phonotactics of one of the languages the system understands.
    </p>

    <p>
      As an example of a rule that looks at stress, consider the English pronunciation dictionary that <Say>/ˈfoʊ̯nim ˌʃɪftɝ/</Say> uses,
      which uses schwa - <Say say="/ˈə/">ə</Say> - in places that often represent different sounds depending on context.
      Let's say our accent pronounces it as <Say say="/ˈʌ/">ʌ</Say> in a stressed syllable, but <Say say="/ˈɪ/">ɪ</Say> in an unstressed syllable.
      So, button: <Say>/ˈbətən/</Say>, where the first <i>ə</i> is in a stressed syllable and the second is unstressed,
      would end up being pronounced <Say>/ˈbʌtɪn/</Say>.
      In the context description, we use <Rule>S</Rule> to indicate a stressed syllable, <Rule>U</Rule> for
      an unstressed syllable, and <Rule>A</Rule> for any type (stressed or unstressed),
      with the entire stress descriptor wrapped in single quotes.
      Thus, our rules could be represented as follows:
    </p>

    <RulesTable rules={[
      { rule: "ə > ʌ / 'S'", other: <>change ə to ʌ in a stressed syllable</> },
      { rule: "ə > ɪ / 'U'", other: <>change ə to ɪ in a unstressed syllable</> },
    ]} />

    <p>
      We can also specify whether syllables before or after the current syllable are stressed.
      Consider aqualung: <Say>/ˈɑkwəˌɫəŋ/</Say>, which turns into <Say>/ˈɑkwɪˌɫʌŋ/</Say> when we apply the two rules from above.
      Let's say we instead want the schwa in the middle to turn into <Say say='ˈʌ'>ʌ</Say> , giving us <Say>ˈɑkwʌˌɫʌŋ</Say>,
      and we've identified that this is because it's part of a set of words where the previous syllable (<Say>ˈɑk</Say>) is
      stressed, the syllable in question (<Say>wə</Say>) is unstressed, and the next syllable (<Say>ˌɫəŋ</Say>) is stressed.
      We capture this pattern as <Rule>'S_U_S'</Rule>, giving us the following rule:
    </p>

    <RulesTable rules={[
      { rule: "ə > ʌ / 'S_U_S'", other: <>change ə to ʌ if previous is stressed, current is unstressed, and next is stressed</> },
    ]} />

    <p>
      Note that rules are run in the order they're given, so if we want our new rule to run before the <Rule>{`ə > ɪ`}</Rule> rule,
      we need to list it earlier.
      Our three rules could then look like this:
    </p>

    <RulesTable rules={[
      { rule: "ə > ʌ / 'S_U_S'", other: <>change if previous is stressed, current is unstressed, and next is stressed</> },
      { rule: "ə > ʌ / 'S'", other: <>change in a stressed syllable</> },
      { rule: "ə > ɪ / 'U'", other: <>change in a unstressed syllable</> },
    ]} />

    <p>
      Note that we can combine any of the context descriptors with stress descriptors.
      So, if we want <i>ə</i> in an unstressed syllable at the start of a word to change to <i>ʌ</i>, we can use the following rule:
    </p>

    <RulesTable rules={[
      { rule: "ə > ʌ / #_ 'U'", other: <>change at the start of a word if current syllable is unstressed</> },
    ]} />


    <h2 id="swap">Swap</h2>

    <p>
      When converting from an orthography (writing system) to IPA,
      you may find that the order in which the symbols occur may not match between the two systems,
      so you might need to swap their positions.
    </p>

    <p>
      For example, in the Japanese writing system of Hiragana, the symbol っ can be put <i>before</i> a
      consonant to indicate that it's longer, such as っし to say that し is longer.
      But in IPA, you put the lengthen symbol (ː) <i>after</i> a phoneme
      to indicate that it's longer, such as [ɕː] for a long [ɕ] sound.
      This means you can't do a simple one-to-one replacement of symbols.
    </p>

    <p>
      To handle this, a rule can also indicate that the resulting symbol should have its
      position swapped with either the previous or next symbol.
      For the Hiragana example, this looks like the following:
    </p>

    <RulesTable rules={[
      { rule: "し > ɕi", other: <>unstressed</> },
      { rule: "っ > ː *(next)", other: <>replace っ with ː and swap it with the next symbol</> },
    ]} />

    <p>
      Thus if you have <POrtho>っし</POrtho>, it first turns <POrtho>し</POrtho> into <PSquare>ɕi</PSquare>,
      then turns <POrtho>っ</POrtho> into <PSquare>ː</PSquare> and swaps it with the following
      symbol <PSquare>ɕ</PSquare>, giving you <PSquare>ɕːi</PSquare>.
    </p>

    <p>
      For another example, consider CXS (<a href="https://en.wikibooks.org/wiki/Conlang/Appendix/CXS">Conlang X-SAMPA</a>).
      Where IPA uses a tie-bar symbol <i>between</i> two symbols to indicate an affricate such as <PSquare>t͡s</PSquare>,
      CXS puts a right-paren <i>after</i> the pair of symbols such as <b>ts)</b>.
      In this case, you need to do a swap with the <i>previous</i> symbol after replacement.
    </p>

    <RulesTable rules={[
      { rule: ") > ͡	*(prev)", other: <>replace ) with a tie-bar and swap it with the previous symbol</> },
    ]} />


    <h2 id="reset">Resetting tracking</h2>

    <p>
      Each symbol is only replaced once for a set of rules.
      This can be useful in avoiding confusion in how your rules are applied,
      especially when a language's orthography and IPA overlap.
      For example, consider an orthography that contains <i>e</i>, <i>i</i>, and <i>y</i> with the following rules:
    </p>
    <RulesTable rules={[
      { rule: "e > i" },
      { rule: "i > y" },
    ]} />
    <p>
      After the first rule runs, you now have <i>i</i>'s that you want to leave as-is,
      without getting modified by the second rule.
      Since the system has already modified those symbols once, it knows not to modify them a second time.
      Thus, <POrtho>bei</POrtho> is changed to <PSquare>biy</PSquare>.
      If you want to start over again in being able to modify any symbol, you can use the <Rule>reset</Rule> command.
    </p>


    <h2 id="summary">Summary</h2>

    <RuleSummary />

  </div>);
}
